import { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { MoreVertical, Tag } from "react-feather";
import {
  Col,
  Row,
  Button,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Badge,
} from "reactstrap";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import axios from "axios";
import exportExcelCustomer from "../../../../Components/Pages/MainPages/Reports/components/exportExcelCustomer";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import ConfirmModal from "../../../ConfirmModal/ConfirmModal";
import CustomerTagModal from "./Tag/CustomerTagModal";
import useRbac from "../../../../Hooks/useRbac";

const CustomerTable = () => {
  const { can, canAny } = useRbac();
  const [isLoading, setIsLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Tag Modal State
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [customerForTags, setCustomerForTags] = useState(null);

  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const navigate = useNavigate();

  const handleSort = (column, sortDirection) => {
    setSortField(column.sortField || "createdAt");
    setSortDirection(sortDirection);
    setCurrentPage(1);
  };

  const handleCustomerClick = (customerId) => {
    const currentIndex = customersData.findIndex(
      (customer) => customer._id === customerId
    );

    const navigationData = {
      currentIndex: (currentPage - 1) * rowsPerPage + currentIndex,
      totalCustomers: totalRows,
      customersList: customersData,
      filters: {
        search: searchTerm,
        page: currentPage,
        limit: rowsPerPage,
      },
    };

    navigate(`/customers/customer-detail/${customerId}`, {
      state: navigationData,
    });
  };

  const handleOpenTagModal = (customer) => {
    setCustomerForTags(customer);
    setTagModalOpen(true);
  };

  const handleTagsUpdated = (customerId, newTags) => {
    // Update the customer in the table with new tags
    setCustomersData((prev) =>
      prev.map((customer) =>
        customer._id === customerId
          ? { ...customer, tags: newTags }
          : customer
      )
    );
  };

  const customerCols = [
    {
      name: "Customer Name",
      width: "180px",
      selector: (row) => {
        if (row?.firstName && row?.lastName) {
          return `${row.firstName} ${row.lastName}`;
        } else if (row?.firstName) {
          return row.firstName;
        } else if (row?.lastName) {
          return row.lastName;
        } else {
          return "";
        }
      },
      cell: (row) => {
        const fullName =
          row?.firstName && row?.lastName
            ? `${row.firstName} ${row.lastName}`
            : row?.firstName || row?.lastName || "N/A";

        return (
          <div
            onClick={() => handleCustomerClick(row._id)}
            style={{
              cursor: "pointer",
              color: "#007bff",
              textDecoration: "none",
              fontWeight: "500",
            }}
            className="customer-name-link"
            onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
            onMouseOut={(e) => (e.target.style.textDecoration = "none")}
          >
            {fullName}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Customer Email",
      selector: (row) => row?.email,
      width: "240px",
      cell: (row) => (row?.email === null ? "NA" : row?.email),
      sortable: true,
    },
    {
      name: "Tags",
      width: "250px",
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1" style={{ padding: "4px 0" }}>
          {row?.tags && row.tags.length > 0 ? (
            <>
              {row.tags.slice(0, 2).map((tag, index) => (
                <Badge
                  key={index}
                  style={{
                    backgroundColor: tag.color || "#6B7280",
                    color: "#fff",
                    fontSize: "0.75rem",
                    padding: "4px 8px",
                  }}
                >
                  {tag.display_name || tag.name}
                </Badge>
              ))}
              {row.tags.length > 2 && (
                <Badge
                  color="light"
                  style={{
                    fontSize: "0.75rem",
                    padding: "4px 8px",
                  }}
                >
                  +{row.tags.length - 2}
                </Badge>
              )}
            </>
          ) : (
            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
              No tags
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Orders",
      width: "100px",
      selector: (row) => (row?.totalOrders ? row?.totalOrders : 0),
      sortable: true,
      sortField: "totalOrders",
    },
    {
      name: "Amount Paid",
      selector: (row) =>
        row?.totalAmount ? "$" + row?.totalAmount.toFixed(2) : `$0`,
      sortable: true,
      sortField: "totalAmount",
    },
    {
      name: "Address",
      width: "300px",
      selector: (row) =>
        row?.address?.address ? row?.address?.address : "N/A",
      cell: (row) => (
        <div style={{ textAlign: "left" }} className="w-full text-left">
          {row?.address?.address ? row?.address?.address : "N/A"}
        </div>
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Status",
      selector: (row) => row?.isBlocked,
      cell: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "12px",
            backgroundColor: row?.isBlocked ? "#f8d7da" : "#d4edda",
            color: row?.isBlocked ? "#721c24" : "#155724",
            fontWeight: 500,
            fontSize: "0.85rem",
          }}
        >
          {row?.isBlocked ? "Blocked" : "Active"}
        </span>
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Last Order",
      width: "120px",
      selector: (row) =>
        row?.lastOrderDate
          ? new Date(row.lastOrderDate).toLocaleDateString()
          : "N/A",
      cell: (row) => (
        <div>
          {row?.lastOrderDate
            ? new Date(row.lastOrderDate).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A"}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      omit: !canAny(['customers.change_status', 'customers.delete']),
      width: "90px",
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => handleOpenTagModal(row)}>
              <Tag size={14} className="me-2" />
              Manage Tags
            </DropdownItem>
            {can('customers.change_status') && <DropdownItem
              className={row?.isBlocked ? "text-success" : "text-danger"}
              onClick={() => handleBlockToggle(row)}
            >
              {row?.isBlocked ? "Unblock" : "Block"}
            </DropdownItem>}
            {can('customers.delete') && <DropdownItem
              className="text-danger"
              onClick={() => handleDeleteClick(row)}
            >
              <span>Delete</span>
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      sortable: false,
      center: true,
    },
  ];

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const token = await JSON.parse(localStorage.getItem("token"));

      const response = await axios.get(
        `${baseURL}/api/customers/get-customers-list`,
        {
          params: {
            page: currentPage,
            limit: rowsPerPage,
            search: debouncedSearch,
            order_by: sortField,
            order_type: sortDirection,
          },
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.status === 200) {
        const customersData = response?.data?.data?.customerList;
        setCustomersData(customersData);
        setTotalRows(response.data.data?.totalRows || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, rowsPerPage, debouncedSearch, sortField, sortDirection]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const downloadExcelCustomerData = async () => {
    try {
      setIsExcelLoading(true);
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.get(
        `${baseURL}/api/customers/export-customers`,
        {
          params: {
            search: debouncedSearch,
            order_by: sortField,
            order_type: sortDirection,
          },
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.status === 200) {
        exportExcelCustomer(response.data.data.customerList);
      }
    } catch (err) {
      console.error("Excel export failed", err);
    } finally {
      setIsExcelLoading(false);
    }
  };

  const handleBlockToggle = async (row) => {
    const userId = row._id;
    const shouldBlock = !row.isBlocked;

    try {
      const token = await JSON.parse(localStorage.getItem("token"));
      const response = await axios.post(
        `${baseURL}/api/customers/block-or-unblock/${userId}`,
        {
          block: shouldBlock,
        },
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setCustomersData((prevData) =>
          prevData.map((customer) =>
            customer._id === userId
              ? { ...customer, isBlocked: shouldBlock }
              : customer
          )
        );

        toast.success(
          `Customer ${shouldBlock ? "blocked" : "unblocked"} successfully`
        );
      }
    } catch (error) {
      toast.error("Action failed. Please try again.");
      console.error("Error toggling block:", error);
    }
  };

  const handleDeleteClick = (customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      await axios.delete(
        `${baseURL}/api/customers/delete-customer/${selectedCustomer._id}`,
        { headers: { Authorization: token } }
      );
      setCustomersData((prev) =>
        prev.filter((customer) => customer._id !== selectedCustomer._id)
      );
      toast.success("Customer deleted successfully.");
      setModalOpen(false);
      setSelectedCustomer(null);
    } catch (error) {
      toast.error("Customer deletion failed, Please try again.");
      console.error("error customer deletion", error);
    }
  };

  return (
    <Fragment>
      <Row xxl={12} className="pb-2">
        <Col
          md={12}
          className="d-flex justify-content-between align-items-center mb-3"
        >
          <h5 className="font-bold">Customers</h5>
          <div className="d-flex align-items-center">
            <div className="mb-0 form-group position-relative search_outer d-flex align-items-center me-2">
              <i className="fa fa-search"></i>
              <input
                className="form-control border-0 ms-2"
                value={searchTerm}
                onChange={handleSearch}
                type="text"
                placeholder="Search..."
              />
            </div>
            <Button
              type="button"
              className="btn btn-primary d-flex align-items-center"
              style={{ minWidth: "136px" }}
              onClick={downloadExcelCustomerData}
              disabled={isExcelLoading}
            >
              {isExcelLoading ? (
                <div className="flex justify-center items-center gap-2">
                  <div
                    className="spinner-border spinner-border-sm text-light"
                    role="status"
                  ></div>
                  <span className="">Exporting...</span>
                </div>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="18"
                    viewBox="0 0 14 18"
                    fill="none"
                  >
                    <path
                      d="M9.33882 8.82071L9.33871 8.82059L9.32968 8.82962L8.125 10.0341V5.75L11.687 5.75C11.6871 5.75 11.6872 5.75 11.6874 5.75C12.1348 5.75058 12.5637 5.92857 12.8801 6.24494C13.1965 6.56141 13.3745 6.99049 13.375 7.43803V15.562C13.3745 16.0095 13.1965 16.4386 12.8801 16.7551C12.5636 17.0715 12.1345 17.2495 11.687 17.25H2.31303C1.86549 17.2495 1.43641 17.0715 1.11994 16.7551C0.803539 16.4387 0.625547 16.0097 0.625 15.5622V7.43778C0.625547 6.99032 0.80354 6.56135 1.11994 6.24494C1.43631 5.92857 1.86523 5.75058 2.31263 5.75C2.31277 5.75 2.3129 5.75 2.31303 5.75L5.875 5.75V10.0341L4.67032 8.82962L4.67043 8.8295L4.66118 8.82071C4.44849 8.61864 4.16527 8.50765 3.87192 8.51141C3.57855 8.51516 3.29827 8.63337 3.09082 8.84082C2.88337 9.04827 2.76516 9.32856 2.76141 9.62191C2.75765 9.91527 2.86864 10.1985 3.07071 10.4112L3.07059 10.4113L3.07965 10.4204L6.20465 13.5454L6.20477 13.5455C6.41572 13.7563 6.70176 13.8747 7 13.8747C7.29824 13.8747 7.58428 13.7563 7.79523 13.5455L7.79535 13.5454L10.9204 10.4204L10.9205 10.4205L10.9293 10.4112C11.1314 10.1985 11.2423 9.91527 11.2386 9.62191C11.2348 9.32855 11.1166 9.04827 10.9092 8.84082C10.7017 8.63337 10.4214 8.51516 10.1281 8.51141C9.83473 8.50765 9.55151 8.61864 9.33882 8.82071ZM7.08839 0.786612C7.11183 0.810053 7.125 0.841847 7.125 0.875V4.75H6.875V0.875C6.875 0.841849 6.88817 0.810054 6.91161 0.786612C6.93505 0.763169 6.96685 0.75 7 0.75C7.03315 0.75 7.06495 0.76317 7.08839 0.786612Z"
                      fill="white"
                      stroke="white"
                    />
                  </svg>
                  <span className="ms-2">Export Excel</span>
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <DataTable
            data={customersData}
            columns={customerCols}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={rowsPerPage}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            onChangeRowsPerPage={handlePerPageChange}
            sortServer
            onSort={handleSort}
          />

          <ConfirmModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            onConfirm={handleConfirmDelete}
            title={`Delete "${selectedCustomer?.firstName}"?`}
            description="Are you sure you want to delete this customer? This action cannot be undone."
          />

          {/* Tag Management Modal */}
          <CustomerTagModal
            isOpen={tagModalOpen}
            toggle={() => setTagModalOpen(false)}
            customer={customerForTags}
            onTagsUpdated={handleTagsUpdated}
          />
        </>
      )}
    </Fragment>
  );
};

export default CustomerTable;






// import { Fragment, useEffect, useState } from "react";
// import DataTable from "react-data-table-component";
// import { MoreVertical } from "react-feather";
// import { Col, Row, Button, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from 'reactstrap';
// import { baseURL } from "../../../../Services/api/baseURL";
// import Loader from "../../../Loader/Loader";
// import axios from "axios";
// import exportExcelCustomer from '../../../../Components/Pages/MainPages/Reports/components/exportExcelCustomer'
// import { toast } from 'react-toastify';
// import { useNavigate } from "react-router";
// import ConfirmModal from "../../../ConfirmModal/ConfirmModal";


// const CustomerTable = () => {
//     const [isLoading, setIsLoading] = useState(false);
//     const [isExcelLoading, setIsExcelLoading] = useState(false);
//     const [customersData, setCustomersData] = useState([]);
//     const [searchTerm, setSearchTerm] = useState("");
//     const [currentPage, setCurrentPage] = useState(1);
//     const [rowsPerPage, setRowsPerPage] = useState(10);
//     const [totalRows, setTotalRows] = useState(0);
//     const [selectedCustomer, setSelectedCustomer] = useState(null);
//     const [modalOpen, setModalOpen] = useState(false);

//     const [sortField, setSortField] = useState('createdAt');
//     const [sortDirection, setSortDirection] = useState('desc');
//     const [debouncedSearch, setDebouncedSearch] = useState("");
//     const navigate = useNavigate();

//     const handleSort = (column, sortDirection) => {
//         setSortField(column.sortField || "createdAt");
//         setSortDirection(sortDirection);
//         setCurrentPage(1);
//     };

//     // const handleCustomerClick = (customerId) => {
//     //     navigate(`/customers/customer-detail/${customerId}`);
//     // };
//     const handleCustomerClick = (customerId) => {
//         const currentIndex = customersData.findIndex(customer => customer._id === customerId);

//         const navigationData = {
//             currentIndex: (currentPage - 1) * rowsPerPage + currentIndex,
//             totalCustomers: totalRows,
//             customersList: customersData,
//             filters: {
//                 search: searchTerm,
//                 page: currentPage,
//                 limit: rowsPerPage
//             }
//         };

//         navigate(`/customers/customer-detail/${customerId}`, {
//             state: navigationData
//         });
//     };

//     const customerCols = [
//         {
//             name: "Customer Name",
//             selector: row => {
//                 if (row?.firstName && row?.lastName) {
//                     return `${row.firstName} ${row.lastName}`;
//                 } else if (row?.firstName) {
//                     return row.firstName;
//                 } else if (row?.lastName) {
//                     return row.lastName;
//                 } else {
//                     return "";
//                 }
//             },
//             cell: (row) => {
//                 const fullName = row?.firstName && row?.lastName
//                     ? `${row.firstName} ${row.lastName}`
//                     : row?.firstName || row?.lastName || "N/A";

//                 return (
//                     <div
//                         onClick={() => handleCustomerClick(row._id)}
//                         style={{
//                             cursor: 'pointer',
//                             color: '#007bff',
//                             textDecoration: 'none',
//                             fontWeight: '500'
//                         }}
//                         className="customer-name-link"
//                         onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
//                         onMouseOut={(e) => e.target.style.textDecoration = 'none'}
//                     >
//                         {fullName}
//                     </div>
//                 );
//             },
//             sortable: true,
//         },

//         {
//             name: "Customer Email",
//             selector: row => row?.email,
//             width: "250px",
//             cell: (row) => (
//                 row?.email === null ? "NA" : row?.email
//             ),
//             sortable: true,
//         },
//         {
//             name: "Orders",
//             width: "100px",
//             selector: row => row?.totalOrders ? row?.totalOrders : 0,
//             sortable: true,
//             sortField: "totalOrders"
//         },
//         {
//             name: "Amount Paid",
//             selector: row => row?.totalAmount ? '$' + row?.totalAmount.toFixed(2) : `$0`,
//             sortable: true,
//             sortField: "totalAmount"
//         },
//         {
//             name: "Address",
//             width: "300px",
//             selector: row => row?.address?.address ? row?.address?.address : 'N/A',
//             cell: (row) => (
//                 <div style={{ textAlign: 'left' }} className="w-full text-left">
//                     {row?.address?.address ? row?.address?.address : 'N/A'}
//                 </div>
//             ),
//             sortable: true,
//             center: true,
//         },
//         {
//             name: "Status",
//             selector: (row) => row?.isBlocked,
//             cell: (row) => (
//                 <span
//                     style={{
//                         padding: "4px 10px",
//                         borderRadius: "12px",
//                         backgroundColor: row?.isBlocked ? "#f8d7da" : "#d4edda",
//                         color: row?.isBlocked ? "#721c24" : "#155724",
//                         fontWeight: 500,
//                         fontSize: "0.85rem"
//                     }}
//                 >
//                     {row?.isBlocked ? "Blocked" : "Active"}
//                 </span>
//             ),
//             sortable: true,
//             center: true,
//         },
//         {
//             name: "Last Order",
//             selector: row => row?.lastOrderDate ? new Date(row.lastOrderDate).toLocaleDateString() : "N/A",
//             cell: (row) => (
//                 <div>
//                     {row?.lastOrderDate
//                         ? new Date(row.lastOrderDate).toLocaleDateString("en-IN", {
//                             day: "2-digit",
//                             month: "short",
//                             year: "numeric"
//                         })
//                         : "N/A"}
//                 </div>
//             ),
//             sortable: true,
//         },
//         {
//             name: "Actions",
//             width: "100px",
//             cell: (row) => (
//                 <UncontrolledDropdown className="action_dropdown" >
//                     <DropdownToggle className="action_btn">
//                         <MoreVertical color="#000" size={16} />
//                     </DropdownToggle>
//                     <DropdownMenu>
//                         <DropdownItem
//                             className={row?.isBlocked ? "text-success" : "text-danger"}
//                             onClick={() => handleBlockToggle(row)}
//                         >
//                             {row?.isBlocked ? "Unblock" : "Block"}
//                         </DropdownItem>
//                         <DropdownItem
//                             className="text-danger"
//                             onClick={() => handleDeleteClick(row)}
//                         >

//                             <span>Delete</span>
//                         </DropdownItem>
//                     </DropdownMenu>
//                 </UncontrolledDropdown>
//             ),
//             sortable: false,
//             center: true,
//         }
//     ];

//     const fetchCustomers = async () => {
//         setIsLoading(true);
//         try {
//             const token = await JSON.parse(localStorage.getItem("token"));

//             const response = await axios.get(
//                 `${baseURL}/api/customers/get-customers-list`,
//                 {
//                     params: {
//                         page: currentPage,
//                         limit: rowsPerPage,
//                         search: debouncedSearch,
//                         order_by: sortField,
//                         order_type: sortDirection,
//                     },
//                     headers: {
//                         Authorization: `${token}`,
//                     },
//                 }
//             );

//             if (response.status === 200) {
//                 const customersData = response?.data?.data?.customerList;
//                 setCustomersData(customersData);
//                 setTotalRows(response.data.data?.totalRows || 0);
//             }
//         } catch (e) {
//             console.error(e);
//         } finally {
//             setIsLoading(false);
//         }
//     };


//     useEffect(() => {
//         const handler = setTimeout(() => {
//             setDebouncedSearch(searchTerm);
//         }, 1000); // wait 500ms after user stops typing

//         return () => {
//             clearTimeout(handler); // cancel timeout if user keeps typing
//         };
//     }, [searchTerm]);
//     useEffect(() => {
//         fetchCustomers();
//     }, [currentPage, rowsPerPage, debouncedSearch, sortField, sortDirection]);


//     const handleSearch = (e) => {
//         setSearchTerm(e.target.value);
//         setCurrentPage(1)
//     };

//     const handlePageChange = (page) => {
//         setCurrentPage(page);
//     };
//     const handlePerPageChange = (newPerPage) => {
//         setRowsPerPage(newPerPage);
//         setCurrentPage(1);
//     };

//     const downloadExcelCustomerData = async () => {
//         try {
//             setIsExcelLoading(true)
//             const token = JSON.parse(localStorage.getItem("token"));
//             const response = await axios.get(
//                 `${baseURL}/api/customers/export-customers`,
//                 {
//                     params: {
//                         search: debouncedSearch,
//                         order_by: sortField,
//                         order_type: sortDirection
//                     },
//                     headers: {
//                         Authorization: `${token}`,
//                     },
//                 }
//             );

//             if (response.status === 200) {

//                 exportExcelCustomer(response.data.data.customerList);
//             }
//         } catch (err) {
//             console.error("Excel export failed", err);
//         } finally {
//             setIsExcelLoading(false)
//         }
//     };




//     const handleBlockToggle = async (row) => {
//         const userId = row._id;
//         const shouldBlock = !row.isBlocked;

//         try {
//             const token = await JSON.parse(localStorage.getItem('token'));
//             const response = await axios.post(
//                 `${baseURL}/api/customers/block-or-unblock/${userId}`,
//                 {
//                     block: shouldBlock
//                 },
//                 {
//                     headers: {
//                         Authorization: `${token}`,
//                         'Content-Type': 'application/json'
//                     }
//                 },
//             );

//             if (response.status === 200) {
//                 setCustomersData(prevData =>
//                     prevData.map(customer =>
//                         customer._id === userId
//                             ? { ...customer, isBlocked: shouldBlock }
//                             : customer
//                     )
//                 );

//                 toast.success(`Customer ${shouldBlock ? "blocked" : "unblocked"} successfully`);
//             }
//         } catch (error) {
//             toast.error("Action failed. Please try again.");
//             console.error("Error toggling block:", error);
//         }
//     };
//     const handleDeleteClick = (customer) => {

//         setSelectedCustomer(customer);
//         setModalOpen(true);
//     };
//     const handleConfirmDelete = async () => {
//         try {

//             const token = JSON.parse(localStorage.getItem("token"));
//             await axios.delete(`${baseURL}/api/customers/delete-customer/${selectedCustomer._id}`,
//                 { headers: { Authorization: token } }
//             )
//             setCustomersData((prev) => prev.filter((customer) => customer._id !== selectedCustomer._id))
//             toast.success("Customer deleted successfully.");
//             setModalOpen(false);
//             setSelectedCustomer(null);

//         } catch (error) {
//             toast.error("Customer deletion failed, Please try again.")
//             console.error("error customer deletion", error)
//         }
//     }


//     return (
//         <Fragment>
//             <Row xxl={12} className='pb-2'>
//                 <Col md={12} className='d-flex justify-content-between align-items-center mb-3'>
//                     <h5 className="font-bold">Customers</h5>
//                     <div className="d-flex align-items-center">
//                         <div className="mb-0 form-group position-relative search_outer d-flex align-items-center me-2">
//                             <i className="fa fa-search"></i>
//                             <input
//                                 className="form-control border-0 ms-2"
//                                 value={searchTerm}
//                                 onChange={handleSearch}
//                                 type="text"
//                                 placeholder="Search..."
//                             />
//                         </div>
//                         <Button
//                             type='button'
//                             className='btn btn-primary d-flex align-items-center'
//                             style={{ minWidth: '136px' }}
//                             onClick={downloadExcelCustomerData}
//                             disabled={isExcelLoading} // prevent double clicks
//                         >
//                             {isExcelLoading ? (
//                                 <div className="flex justify-center items-center gap-2">
//                                     <div className="spinner-border spinner-border-sm text-light" role="status">
//                                     </div>
//                                     <span className="">Exporting...</span>
//                                 </div>
//                             ) : (
//                                 <>
//                                     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="18" viewBox="0 0 14 18" fill="none">
//                                         <path d="M9.33882 8.82071L9.33871 8.82059L9.32968 8.82962L8.125 10.0341V5.75L11.687 5.75C11.6871 5.75 11.6872 5.75 11.6874 5.75C12.1348 5.75058 12.5637 5.92857 12.8801 6.24494C13.1965 6.56141 13.3745 6.99049 13.375 7.43803V15.562C13.3745 16.0095 13.1965 16.4386 12.8801 16.7551C12.5636 17.0715 12.1345 17.2495 11.687 17.25H2.31303C1.86549 17.2495 1.43641 17.0715 1.11994 16.7551C0.803539 16.4387 0.625547 16.0097 0.625 15.5622V7.43778C0.625547 6.99032 0.80354 6.56135 1.11994 6.24494C1.43631 5.92857 1.86523 5.75058 2.31263 5.75C2.31277 5.75 2.3129 5.75 2.31303 5.75L5.875 5.75V10.0341L4.67032 8.82962L4.67043 8.8295L4.66118 8.82071C4.44849 8.61864 4.16527 8.50765 3.87192 8.51141C3.57855 8.51516 3.29827 8.63337 3.09082 8.84082C2.88337 9.04827 2.76516 9.32856 2.76141 9.62191C2.75765 9.91527 2.86864 10.1985 3.07071 10.4112L3.07059 10.4113L3.07965 10.4204L6.20465 13.5454L6.20477 13.5455C6.41572 13.7563 6.70176 13.8747 7 13.8747C7.29824 13.8747 7.58428 13.7563 7.79523 13.5455L7.79535 13.5454L10.9204 10.4204L10.9205 10.4205L10.9293 10.4112C11.1314 10.1985 11.2423 9.91527 11.2386 9.62191C11.2348 9.32855 11.1166 9.04827 10.9092 8.84082C10.7017 8.63337 10.4214 8.51516 10.1281 8.51141C9.83473 8.50765 9.55151 8.61864 9.33882 8.82071ZM7.08839 0.786612C7.11183 0.810053 7.125 0.841847 7.125 0.875V4.75H6.875V0.875C6.875 0.841849 6.88817 0.810054 6.91161 0.786612C6.93505 0.763169 6.96685 0.75 7 0.75C7.03315 0.75 7.06495 0.76317 7.08839 0.786612Z" fill="white" stroke="white" />
//                                     </svg>
//                                     <span className="ms-2">Export Excel</span>
//                                 </>
//                             )}
//                         </Button>

//                     </div>
//                 </Col>
//             </Row>

//             {isLoading ? (
//                 <Loader />
//             ) : (
//                 <>
//                     <DataTable
//                         data={customersData}
//                         columns={customerCols}
//                         pagination
//                         paginationServer
//                         paginationTotalRows={totalRows}
//                         paginationPerPage={rowsPerPage}
//                         paginationDefaultPage={currentPage}
//                         onChangePage={handlePageChange}
//                         onChangeRowsPerPage={handlePerPageChange}
//                         sortServer
//                         onSort={handleSort}
//                     />

//                     <ConfirmModal
//                         isOpen={modalOpen}
//                         onClose={() => setModalOpen(false)}
//                         onConfirm={handleConfirmDelete}
//                         title={`Delete "${selectedCustomer?.firstName}"?`}
//                         description="Are you sure you want to delete this customer? This action cannot be undone."
//                     />
//                 </>

//             )}
//         </Fragment>
//     );
// };

// export default CustomerTable;